import { Injectable } from '@angular/core';
import { Subject } from 'rxjs';
import { OfferCategory } from '../model/offer-category.model';

export type AttentionFlashTab = OfferCategory;

export type AttentionFlashTarget =
  | { kind: 'nav-tab'; tab: AttentionFlashTab }
  | { kind: 'timer-row' };

export type AttentionFlashEvent = {
  target: AttentionFlashTarget;
  active: boolean;
};

@Injectable({
  providedIn: 'root',
})
export class AttentionFlashService {
  /** Emits on/off events for a target so components can toggle the `.attention-flash` class. */
  private readonly flashSubject = new Subject<AttentionFlashEvent>();
  public readonly flash$ = this.flashSubject.asObservable();

  private running = false;
  private timeouts: number[] = [];
  // Track currently-active targets so we can immediately turn them off when a new one starts
  private activeMap = new Map<string, AttentionFlashTarget>();
  // Track pending "off" timeouts per target key so they can be cleared when interrupted
  private offTimeouts = new Map<string, number>();

  /** Matches the demo timing in `src/test-components/.html`. */
  // Demo starts at 1000ms; we intentionally delay an extra second for the app UX.
  private readonly initialDelayMs = 0;
  private readonly stepDelayMs = 400;
  private readonly flashDurationMs = 1400;

  /**
   * Starts the attention sequence:
   * 1) flashes each provided tab (in order)
   * 2) finally flashes the timer row
   */
  startSequenceFromNavBar(visibleTabs: AttentionFlashTab[]): void {
    if (this.running) return;
    this.running = true;

    const steps: AttentionFlashTarget[] = [
      ...visibleTabs.map(tab => ({ kind: 'nav-tab', tab }) as const),
      { kind: 'timer-row' } as const,
    ];

    // Play steps strictly sequentially so each target gets one full pulse.
    let currentIndex = 0;

    const playNext = () => {
      if (currentIndex >= steps.length) {
        this.running = false;
        return;
      }

      const target = steps[currentIndex];
      const key = this.keyFor(target);

      // Turn off any other active targets immediately.
      for (const [otherKey, otherTarget] of Array.from(this.activeMap.entries())) {
        if (otherKey === key) continue;

        try {
          this.flashSubject.next({ target: otherTarget, active: false });
        } catch (e) {
          // ignore
        }

        const offId = this.offTimeouts.get(otherKey);
        if (offId !== undefined) {
          window.clearTimeout(offId);
          this.offTimeouts.delete(otherKey);
        }
        this.activeMap.delete(otherKey);
      }

      // Activate this target
      this.activeMap.set(key, target);
      this.flashSubject.next({ target, active: true });

      // Schedule OFF, then schedule NEXT after stepDelay
      const offId = window.setTimeout(() => {
        try {
          this.flashSubject.next({ target, active: false });
        } catch (e) {
          // ignore
        }

        this.offTimeouts.delete(key);
        this.activeMap.delete(key);

        currentIndex += 1;
        const nextId = window.setTimeout(() => {
          playNext();
        }, this.stepDelayMs);
        this.timeouts.push(nextId);
      }, this.flashDurationMs);

      this.offTimeouts.set(key, offId);
    };

    const startId = window.setTimeout(() => playNext(), this.initialDelayMs);
    this.timeouts.push(startId);
  }

  private keyFor(target: AttentionFlashTarget): string {
    if (target.kind === 'timer-row') return 'timer-row';
    return `nav-tab:${target.tab}`;
  }

  /** Allows callers to re-run the sequence (clears any pending timeouts). */
  restartSequenceFromNavBar(visibleTabs: AttentionFlashTab[]): void {
    this.cancel();
    this.startSequenceFromNavBar(visibleTabs);
  }

  cancel(): void {
    // Clear any scheduled start/next timeouts
    this.timeouts.forEach(id => window.clearTimeout(id));
    this.timeouts = [];

    // Clear any scheduled OFF timeouts
    for (const offId of Array.from(this.offTimeouts.values())) {
      window.clearTimeout(offId);
    }
    this.offTimeouts.clear();

    // Emit OFF for any currently-active targets
    for (const [, target] of Array.from(this.activeMap.entries())) {
      try {
        this.flashSubject.next({ target, active: false });
      } catch (e) {
        // ignore
      }
    }
    this.activeMap.clear();

    this.running = false;
  }
}
